import { css } from '@emotion/react';
import { Fragment } from 'react';
import { Group, Panel, Separator } from 'react-resizable-panels';
import { colors } from '../../../../../theme/colors';
import { DropTarget, LayoutCell, LayoutBoxCell } from '../types';
import { PanelSlot } from './PanelSlot';

const cellContainerStyle = css`
  width: 100%;
  height: 100%;
`;

const separatorBaseStyle = css`
  background: ${colors.neutral[20]};

  &:hover {
    background: ${colors.primary[30]};
  }

  &[data-resize-handle-state='drag'] {
    background: ${colors.primary[40]};
  }
`;

const separatorHorizontalStyle = css`
  ${separatorBaseStyle};
  width: 4px;
  cursor: col-resize;
`;

const separatorVerticalStyle = css`
  ${separatorBaseStyle};
  height: 4px;
  cursor: row-resize;
`;

interface CellRendererProps {
  cell: LayoutCell;
  activeDropTarget?: DropTarget | null;
  onRemovePanel?: (cellId: string) => void;
  onSizesChange?: (boxCellId: string, sizes: number[]) => void;
}

export function CellRenderer({ cell, activeDropTarget, onRemovePanel, onSizesChange }: CellRendererProps) {
  // Panel cell - render the panel slot
  if (cell.type === 'panel') {
    return (
      <PanelSlot
        cell={cell}
        activeDropTarget={activeDropTarget}
        onRemove={onRemovePanel ? () => onRemovePanel(cell.id) : undefined}
      />
    );
  }

  // Box cell - render children with resize handles
  return (
    <BoxCellRenderer
      cell={cell}
      activeDropTarget={activeDropTarget}
      onRemovePanel={onRemovePanel}
      onSizesChange={onSizesChange}
    />
  );
}

interface BoxCellRendererProps {
  cell: LayoutBoxCell;
  activeDropTarget?: DropTarget | null;
  onRemovePanel?: (cellId: string) => void;
  onSizesChange?: (boxCellId: string, sizes: number[]) => void;
}

function BoxCellRenderer({ cell, activeDropTarget, onRemovePanel, onSizesChange }: BoxCellRendererProps) {
  const orientation = cell.direction === 'row' ? 'horizontal' : 'vertical';
  const separatorStyle = cell.direction === 'row' ? separatorHorizontalStyle : separatorVerticalStyle;

  const handleLayoutChanged = (layout: { [panelId: string]: number }) => {
    const newSizes = cell.children.map((child) => layout[`cell-${child.id}`] ?? 100 / cell.children.length);
    onSizesChange?.(cell.id, newSizes);
  };

  return (
    <div css={cellContainerStyle}>
      <Group orientation={orientation} onLayoutChanged={handleLayoutChanged}>
        {cell.children.map((child, index) => (
          <Fragment key={child.id}>
            <Panel
              id={`cell-${child.id}`}
              defaultSize={`${cell.sizes?.[index] ?? 100 / cell.children.length}%`}
              minSize='10%'
            >
              <CellRenderer
                cell={child}
                activeDropTarget={activeDropTarget}
                onRemovePanel={onRemovePanel}
                onSizesChange={onSizesChange}
              />
            </Panel>
            {index < cell.children.length - 1 && <Separator css={separatorStyle} />}
          </Fragment>
        ))}
      </Group>
    </div>
  );
}
